// API Service for RokdaHisab
// Use environment-specific API URL
const API_BASE_URL = process.env.REACT_APP_API_URL 
  || (process.env.NODE_ENV === 'production' 
    ? 'https://api.rokadahisab.com/api' 
    : 'https://localhost:7050/api');

// Types
export interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  isApprovedByAdmin?: boolean;
  isActive?: boolean;
  mobileNumber?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  role: string;
  createdAt: string;
  lastLoginAt?: string;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminAuthResponse {
  token: string;
  email: string;
  role: string;
  expiresAt: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  expiresAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Email verification interfaces
export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  company?: string;
}

export interface Transaction {
  id: number;
  entryDate: string;
  accountId: number;
  accountName: string;
  description: string;
  counterparty?: string;
  amount: number;
  type: number; // 1=Income, 2=Expense, 3=Transfer
  category?: string;
  narration?: string;
  reference?: string;
  tags?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateTransactionRequest {
  entryDate: string;
  accountId: number;
  description: string;
  counterparty?: string;
  amount: number;
  type: number; // 1=Income, 2=Expense, 3=Transfer
  category?: string;
  narration?: string;
  reference?: string;
  tags?: string;
}

export interface DashboardStats {
  totalBalance: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  totalTransactions: number;
  netIncome: number;
  savingsRate: number;
  accountBalances: AccountBalance[];
  categorySummary: CategorySummary[];
}

export interface AccountBalance {
  id: number;
  name: string;
  accountType: string;
  balance: number;
  monthlyChange: number;
}

export interface CategorySummary {
  category: string;
  amount: number;
  transactionCount: number;
  percentage: number;
}

export interface RecentTransaction {
  id: number;
  entryDate: string;
  description: string;
  amount: number;
  type: number; // 1=Income, 2=Expense, 3=Transfer
  category: string;
  accountName: string;
}

export interface FeatureFlagsResponse {
  recurringTransactions: boolean;
}

// API Service Class
class ApiService {
  private baseURL: string;
  private token: string | null = null;
  private onUnauthorizedCallback: (() => void) | null = null;
  private pendingRequests: Set<AbortController> = new Set();
  private inFlightRequests: Map<string, Promise<any>> = new Map();
  private inFlightGetRequests: Map<string, Promise<any>> = new Map();
  private responseCache: Map<string, { data: any; expiresAt: number }> = new Map();
  private requestFrequency: Map<string, number[]> = new Map();

  constructor() {
    this.baseURL = API_BASE_URL;
    this.initializeToken();
  }

  // Register callback for 401 unauthorized responses
  setUnauthorizedCallback(callback: () => void) {
    this.onUnauthorizedCallback = callback;
  }

  // Clear unauthorized callback
  clearUnauthorizedCallback() {
    this.onUnauthorizedCallback = null;
  }

  // Cancel all pending requests
  cancelAllRequests() {
    this.pendingRequests.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {
        // Ignore errors when aborting
      }
    });
    this.pendingRequests.clear();
  }

  private recordRequestFrequency(endpoint: string) {
    const now = Date.now();
    const windowMs = 10_000;
    const warningThreshold = 6;
    const existing = this.requestFrequency.get(endpoint) || [];
    const withinWindow = existing.filter((timestamp) => now - timestamp <= windowMs);
    withinWindow.push(now);
    this.requestFrequency.set(endpoint, withinWindow);

    if (withinWindow.length >= warningThreshold) {
      console.warn(`Frequent API calls detected for ${endpoint}: ${withinWindow.length} calls in ${windowMs / 1000}s`);
    }
  }

  private getRequestMethod(options: RequestInit): string {
    return (options.method || 'GET').toUpperCase();
  }

  private buildCacheKey(endpoint: string, options: RequestInit): string {
    const method = this.getRequestMethod(options);
    return `${method}:${endpoint}`;
  }

  private getCachedResponse<T>(cacheKey: string): T | null {
    const cached = this.responseCache.get(cacheKey);
    if (!cached) {
      return null;
    }

    if (cached.expiresAt <= Date.now()) {
      this.responseCache.delete(cacheKey);
      return null;
    }

    return cached.data as T;
  }

  private setCachedResponse<T>(cacheKey: string, data: T, ttlMs: number) {
    this.responseCache.set(cacheKey, {
      data,
      expiresAt: Date.now() + ttlMs
    });
  }

  private clearAllCaches() {
    this.responseCache.clear();
    this.inFlightGetRequests.clear();
  }

  private clearCacheByPrefix(prefixes: string[]) {
    if (prefixes.length === 0) {
      return;
    }

    Array.from(this.responseCache.keys()).forEach((key) => {
      if (prefixes.some((prefix) => key.includes(prefix))) {
        this.responseCache.delete(key);
      }
    });
  }

  private async getWithCache<T>(endpoint: string, ttlMs: number): Promise<T> {
    const options: RequestInit = { method: 'GET' };
    const cacheKey = this.buildCacheKey(endpoint, options);

    const cached = this.getCachedResponse<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }

    const inFlight = this.inFlightGetRequests.get(cacheKey);
    if (inFlight) {
      return inFlight as Promise<T>;
    }

    const promise = this.request<T>(endpoint, options)
      .then((result) => {
        this.setCachedResponse(cacheKey, result, ttlMs);
        return result;
      })
      .finally(() => {
        this.inFlightGetRequests.delete(cacheKey);
      });

    this.inFlightGetRequests.set(cacheKey, promise);
    return promise;
  }

  private initializeToken() {
    // Check if remember me is enabled
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (rememberMe) {
      // Check localStorage for persistent token
      const token = localStorage.getItem('token');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (token && tokenExpiry && !this.isTokenExpired(tokenExpiry)) {
        this.token = token;
      } else {
        // Token expired, clear it
        this.clearStoredTokens();
      }
    } else {
      // Check sessionStorage for session token
      const token = sessionStorage.getItem('token');
      const tokenExpiry = sessionStorage.getItem('tokenExpiry');
      
      if (token && tokenExpiry && !this.isTokenExpired(tokenExpiry)) {
        this.token = token;
      } else {
        // Token expired, clear it
        this.clearStoredTokens();
      }
    }
  }

  private isTokenExpired(expiryDate: string): boolean {
    try {
      const expiry = new Date(expiryDate);
      return expiry <= new Date();
    } catch {
      return true; // If we can't parse the date, consider it expired
    }
  }

  private clearStoredTokens() {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('userRole');
    this.token = null;
    this.clearAllCaches();
  }

  // Force logout - clears tokens and triggers unauthorized callback
  forceLogout() {
    this.cancelAllRequests();
    this.clearStoredTokens();
    if (this.onUnauthorizedCallback) {
      this.onUnauthorizedCallback();
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Don't make requests if not authenticated (unless it's a public endpoint)
    const isPublicEndpoint = endpoint.includes('/auth/') || endpoint.includes('/admin/login');
    if (!isPublicEndpoint && !this.token) {
      console.warn('API request blocked: No authentication token');
      const error = new Error('Not authenticated. Please login again.') as any;
      error.errorCode = 'UNAUTHORIZED';
      error.status = 401;
      throw error;
    }

    const url = `${this.baseURL}${endpoint}`;
    this.recordRequestFrequency(endpoint);
    const method = this.getRequestMethod(options);
    const requestKey = this.buildCacheKey(endpoint, { ...options, method });

    // Global safeguard: dedupe identical concurrent GET requests.
    if (method === 'GET') {
      const existingRequest = this.inFlightRequests.get(requestKey);
      if (existingRequest) {
        console.log(`Reusing in-flight request for: ${endpoint}`);
        return existingRequest as Promise<T>;
      }
    }
    
    const requestPromise = (async () => {
      // Create AbortController for request cancellation
      const abortController = new AbortController();
      this.pendingRequests.add(abortController);
      
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
          ...options.headers,
        },
        signal: abortController.signal,
        ...options,
      };

      try {
        console.log('Making API request to:', url);
        
        const response = await fetch(url, config);
        
        console.log('Response status:', response.status);
        
        // GLOBAL 401 HANDLER - Critical for account deletion and auth issues
        if (response.status === 401) {
          console.warn('401 Unauthorized - Forcing logout');
          
          // Cancel all pending requests immediately
          this.cancelAllRequests();
          
          // Clear tokens immediately
          this.clearStoredTokens();
          
          // Trigger unauthorized callback (will redirect to login)
          if (this.onUnauthorizedCallback) {
            this.onUnauthorizedCallback();
          }
          
          // Throw error with 401 status
          const error = new Error('Session expired. Please login again.') as any;
          error.errorCode = 'UNAUTHORIZED';
          error.status = 401;
          throw error;
        }
        
        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          let errorCode: string | undefined;
          try {
            const errorData = await response.json();
            console.log('Error response data:', errorData);
            errorMessage = errorData.message || errorMessage;
            errorCode = errorData.errorCode;
          } catch (parseError) {
            const errorText = await response.text();
            console.log('Error response text:', errorText);
            errorMessage = errorText || errorMessage;
          }
          const error = new Error(errorMessage) as any;
          error.errorCode = errorCode;
          error.status = response.status;
          throw error;
        }

        const result = await response.json();
        console.log('API response:', result);
        return result;
      } catch (error: any) {
        // Don't log aborted requests as errors
        if (error.name === 'AbortError') {
          console.log('Request aborted');
          throw error;
        }
        
        console.error('API request failed:', error);
        
        // Handle network errors
        if (error instanceof TypeError && error.message.includes('fetch')) {
          const networkError = new Error('Network issue. Please check your internet connection.') as any;
          networkError.errorCode = 'NETWORK_ERROR';
          throw networkError;
        }
        
        // Re-throw the error (will be handled by errorHandler utility in components)
        throw error;
      } finally {
        // Remove from pending requests
        this.pendingRequests.delete(abortController);
      }
    })();

    if (method === 'GET') {
      this.inFlightRequests.set(requestKey, requestPromise);
      requestPromise.finally(() => {
        this.inFlightRequests.delete(requestKey);
      });
    }

    return requestPromise;
  }

  // Email verification methods
  async verifyEmail(token: string): Promise<{ success: boolean; message: string; errorCode?: string }> {
    return this.request<{ success: boolean; message: string; errorCode?: string }>(`/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Admin methods
  async adminLogin(credentials: AdminLoginRequest): Promise<AdminAuthResponse> {
    const response = await this.request<AdminAuthResponse>('/admin/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    this.token = response.token;
    
    // Store admin token in sessionStorage
    sessionStorage.setItem('token', response.token);
    sessionStorage.setItem('tokenExpiry', response.expiresAt);
    sessionStorage.setItem('userRole', 'ADMIN');
    localStorage.removeItem('rememberMe');
    this.clearAllCaches();
    
    return response;
  }

  async getUsers(): Promise<User[]> {
    return this.getWithCache<User[]>('/admin/users', 30_000);
  }

  async verifyUserEmail(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>(`/admin/users/${userId}/verify-email`, {
      method: 'POST',
    });
    this.clearCacheByPrefix(['/admin/users']);
    return response;
  }

  async activateUser(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>(`/admin/users/${userId}/activate`, {
      method: 'POST',
    });
    this.clearCacheByPrefix(['/admin/users']);
    return response;
  }

  async deactivateUser(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>(`/admin/users/${userId}/deactivate`, {
      method: 'POST',
    });
    this.clearCacheByPrefix(['/admin/users']);
    return response;
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    this.token = response.token;
    
    // Store token with appropriate expiration
    if (credentials.rememberMe) {
      // Store in localStorage for persistent login (30 days)
      localStorage.setItem('token', response.token);
      localStorage.setItem('tokenExpiry', response.expiresAt);
      localStorage.setItem('rememberMe', 'true');
    } else {
      // Store in sessionStorage for session-only login
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('tokenExpiry', response.expiresAt);
      localStorage.removeItem('rememberMe');
    }
    this.clearAllCaches();
    
    return response;
  }

  async register(userData: RegisterRequest): Promise<{ message: string; emailSent: boolean }> {
    // Registration now returns a message, not AuthResponse
    // User must verify email before logging in
    return this.request<{ message: string; emailSent: boolean }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.clearStoredTokens();
    }
  }

  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    const result = await this.request<{ success: boolean; message: string }>('/auth/account/delete', {
      method: 'POST',
    });
    
    // Immediately logout after successful deletion
    // This prevents any further API calls with the deleted account's token
    this.forceLogout();
    
    return result;
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  // Method to refresh authentication state (useful for app initialization)
  refreshAuthState(): boolean {
    this.initializeToken();
    return this.token !== null;
  }

  // Dashboard methods
  async getDashboardStats(): Promise<DashboardStats> {
    return this.getWithCache<DashboardStats>('/dashboard/stats', 20_000);
  }

  async getRecentTransactions(count: number = 5): Promise<RecentTransaction[]> {
    return this.getWithCache<RecentTransaction[]>(`/dashboard/recent-transactions?count=${count}`, 20_000);
  }

  async getFeatureFlags(): Promise<FeatureFlagsResponse> {
    return this.getWithCache<FeatureFlagsResponse>('/feature-flags', 300_000);
  }

  // Account methods
  async getAccounts(): Promise<AccountBalance[]> {
    return this.getWithCache<AccountBalance[]>('/accounts', 30_000);
  }

  async createAccount(account: {
    name: string;
    accountType: string;
    description?: string;
    openingBalance?: number;
  }): Promise<AccountBalance> {
    const response = await this.request<AccountBalance>('/accounts', {
      method: 'POST',
      body: JSON.stringify({
        name: account.name,
        accountType: account.accountType,
        description: account.description,
        openingBalance: account.openingBalance
      }),
    });
    this.clearCacheByPrefix(['/accounts', '/dashboard/stats']);
    return response;
  }

  // Transaction methods
  async getTransactions(params?: {
    fromDate?: string;
    toDate?: string;
    accountId?: number;
    type?: string;
    category?: string;
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<{
    transactions: Transaction[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = searchParams.toString();
    return this.request(`/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getTransaction(id: number): Promise<Transaction> {
    return this.request<Transaction>(`/transactions/${id}`);
  }

  async createTransaction(transaction: CreateTransactionRequest): Promise<Transaction> {
    const response = await this.request<Transaction>('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
    this.clearCacheByPrefix(['/transactions', '/dashboard/stats', '/dashboard/recent-transactions']);
    return response;
  }

  async updateTransaction(id: number, transaction: CreateTransactionRequest): Promise<Transaction> {
    const response = await this.request<Transaction>(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
    this.clearCacheByPrefix(['/transactions', '/dashboard/stats', '/dashboard/recent-transactions']);
    return response;
  }

  async deleteTransaction(id: number): Promise<void> {
    const response = await this.request<void>(`/transactions/${id}`, {
      method: 'DELETE',
    });
    this.clearCacheByPrefix(['/transactions', '/dashboard/stats', '/dashboard/recent-transactions']);
    return response;
  }

  // Utility methods
  getToken(): string | null {
    return this.token;
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
    this.clearAllCaches();
  }

}

// Create and export a singleton instance
export const apiService = new ApiService();
export default apiService;
